import { defineStore } from 'pinia'
import type { RouteLocationNormalized } from 'vue-router'
import { pathWithoutHash } from '~/assets/util'

type NavItem = { id?: string; url?: string; title?: string; children?: NavItem[]; [key: string]: unknown }

/** A navigation item after annotation: `children`, `isOpen` and `isActive` are always filled in. */
type AnnotatedNavItem = NavItem & { children: AnnotatedNavItem[]; isOpen: boolean; isActive: boolean }

/**
 * Navigation state. Passive: the `global` store fetches the data and calls `setNavigation`. The getters
 * annotate items with open/active flags derived from the current path + open item.
 */
const annotateNavigationItems = (items: NavItem[] | undefined, openItemId: string | undefined, fullPath: string | undefined): AnnotatedNavItem[] => {
  return (
    items?.map((item) => {
      const children = annotateNavigationItems(item.children, openItemId, fullPath)
      return {
        ...item,
        isOpen: children.length > 0 && (children.some((child) => child.isOpen) || item.id === openItemId),
        isActive: children.some((child) => child.isActive) || (!!item.url && item.url?.match(/^(https?:\/\/[^/]+)?(?<fullPath>.*)/i)?.groups?.fullPath === fullPath),
        children,
      }
    }) || []
  )
}

export const useNavigationStore = defineStore('navigation', {
  state: () => ({
    // The current path, used to determine what the active navigation element is.
    fullPath: undefined as string | undefined,
    // The id of the current navigation item that is toggled open.
    openItemId: undefined as string | undefined,
    // Navigation elements (set by the global store).
    navigation: { bottom: [], footer: [], menu: [], newsletter: [], service: [] } as Record<string, NavItem[]>,
  }),
  getters: {
    bottom: (state) => annotateNavigationItems(state.navigation.bottom, state.openItemId, state.fullPath),
    footer: (state) => annotateNavigationItems(state.navigation.footer, state.openItemId, state.fullPath),
    menu: (state) => annotateNavigationItems(state.navigation.menu, state.openItemId, state.fullPath),
    newsletter: (state) => annotateNavigationItems(state.navigation.newsletter, state.openItemId, state.fullPath),
    service: (state) => annotateNavigationItems(state.navigation.service, state.openItemId, state.fullPath),
  },
  actions: {
    closeAll(exclude: (string | undefined)[] = []) {
      if (this.openItemId === undefined || exclude.includes(this.openItemId)) {
        return
      }
      this.openItemId = undefined
    },
    setNavigation(navigation: Record<string, NavItem[]>) {
      this.navigation = navigation
    },
    setRoute(route: RouteLocationNormalized) {
      // Without the fragment, or the active item would differ between the server and the client.
      this.fullPath = pathWithoutHash(route.fullPath)
    },
    setOpenItem(id: string | undefined) {
      this.openItemId = id
    },
    toggleOpen(id: string | undefined) {
      this.openItemId = this.openItemId === id ? undefined : id
    },
  },
})
